import React from 'react';
import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import { Button } from '@ui/button';
import { Dropdown } from '@web/atoms';
import { ResponsiveImage } from '@web/molecules';
import { useReporter } from '../../reporter';
import { ctaButtonClicked } from './events';
import {
  Container,
  Content,
  Header,
  BodyText,
  CTAContianer,
  DropdownContainer,
  DropdownDetailsText,
  OptionSavedContainer,
  OptionSavedText,
  OptionSavedDetails,
  ImageWrapper,
  ButtonContainer,
  Highlight,
} from './styles';

export const DropdownConfirmationBanner = ({
  highlight = false,
  header,
  description,
  image,
  cta,
  defaultValues,
  dropdownName,
  options,
  savedText,
  savedDetails,
  optionSaved,
  buttonText,
  onSubmit,
}) => {
  const reporter = useReporter();
  const {
    control,
    watch,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm({
    defaultValues,
    mode: 'onBlur',
  });

  const handleFormSubmit = value => {
    onSubmit(value[dropdownName]);
    if (cta) {
      reporter.tag(ctaButtonClicked(cta?.[0]?.text, header));
    }
  };

  const currentSelectedValue = watch(dropdownName);
  const currentOption = options?.find(
    choice => choice.value === currentSelectedValue,
  );

  return (
    <>
      <Container>
        <ImageWrapper>
          <ResponsiveImage
            src={image.src}
            alt={image.alt}
            fill={true}
            objectFit="cover"
          />
        </ImageWrapper>
        <Content>
          {highlight && <Highlight>{highlight}</Highlight>}
          <Header>{header}</Header>
          <BodyText>{description}</BodyText>
          {!optionSaved ? (
            <CTAContianer>
              <DropdownContainer>
                <Controller
                  render={({
                    field: { onChange, value, ref, ...field },
                    fieldState: { error },
                  }) => (
                    <Dropdown
                      {...field}
                      options={options}
                      selected={value}
                      onChange={onChange}
                    />
                  )}
                  control={control}
                  name={dropdownName}
                />
                {!!currentOption?.details && (
                  <DropdownDetailsText>
                    {currentOption.details}
                  </DropdownDetailsText>
                )}
              </DropdownContainer>
              {cta && (
                <ButtonContainer>
                  <Button
                    onClick={handleSubmit(handleFormSubmit)}
                    disabled={isSubmitting}
                    fullWidth={true}
                  >
                    {buttonText ? buttonText : cta[0].text}
                  </Button>
                </ButtonContainer>
              )}
            </CTAContianer>
          ) : (
            <OptionSavedContainer>
              <OptionSavedText>{savedText}</OptionSavedText>
              <OptionSavedDetails>{savedDetails}</OptionSavedDetails>
            </OptionSavedContainer>
          )}
        </Content>
      </Container>
    </>
  );
};

DropdownConfirmationBanner.displayName = 'DropdownConfirmationBanner';
DropdownConfirmationBanner.propTypes = {
  highlight: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  header: PropTypes.string.isRequired,
  description: PropTypes.node.isRequired,
  promoPillText: PropTypes.string,
  image: PropTypes.object.isRequired,
  cta: PropTypes.array,
  dropdownName: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  savedText: PropTypes.string,
  savedDetails: PropTypes.string,
  optionSaved: PropTypes.bool,
  onSubmit: PropTypes.func,
};
